import { useState } from "react";
import { Bookmark, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { joinCommunity, trackCtaClick } from "../lib/api";
import { trackClickButton } from "../lib/tiktok";
import { toast } from "sonner";

/** Soft email-capture card for `/sample` pages. Targets warm leads who
 *  aren't ready to buy yet. Persists to the same `community_emails`
 *  collection but with `source="sample-lead-{locale}"` so the admin can
 *  segment them apart from active community signups. */
export default function SampleLeadCapture({ locale = "gulf" }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const source = `sample-lead-${locale}`;
  const isEg = locale === "eg";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const cleaned = email.trim();
    if (!cleaned || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned)) {
      toast.error(isEg ? "من فضلك اكتب بريد إلكتروني صحيح." : "الرجاء كتابة بريد إلكتروني صحيح.");
      return;
    }
    setSubmitting(true);
    trackCtaClick(source);
    trackClickButton(source);
    try {
      await joinCommunity(cleaned, null, source);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isEg ? "حدث خطأ، حاول تاني." : "حدث خطأ، حاول مجدداً."));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="bg-[#0B1D3A]/70 border border-[#C5A059]/40 rounded-sm p-6 sm:p-8 text-center shadow-[0_0_40px_rgba(197,160,89,0.18)]"
        data-testid="sample-lead-success"
        dir="rtl"
      >
        <div className="flex justify-center mb-3">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/50 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 sm:w-7 sm:h-7 text-[#C5A059]" strokeWidth={1.8} />
          </div>
        </div>
        <h3 className="font-display text-lg sm:text-2xl font-black">
          <span className="text-[#C5A059]">تم الحفظ</span> بنجاح
        </h3>
        <p className="mt-3 text-sm text-[#A1B0C6] leading-relaxed">
          {isEg
            ? "هتلاقي العينة في بريدك خلال دقايق + هتوصلك عروض حصرية لما تبقى جاهز."
            : "ستجد العينة في بريدك خلال دقائق، وستصلك عروض حصرية متى ما كنت جاهزاً."}
          <br />
          <span className="text-white font-bold inline-block mt-1" style={{ direction: "ltr" }}>{email}</span>
        </p>
      </div>
    );
  }

  return (
    <div
      className="relative bg-gradient-to-br from-[#0B1D3A]/80 via-[#0B1D3A]/60 to-transparent border border-[#C5A059]/30 rounded-sm p-6 sm:p-8 shadow-[0_0_40px_rgba(197,160,89,0.12)]"
      data-testid="sample-lead-capture"
      dir="rtl"
    >
      <span className="absolute -top-3 right-5 bg-[#C5A059] text-[#040914] text-[10px] sm:text-xs font-black px-3 py-1 rounded-full shadow-md tracking-wide">
        مجاناً
      </span>

      <div className="flex items-start gap-3 mb-3">
        <Bookmark className="w-5 h-5 sm:w-6 sm:h-6 text-[#C5A059] shrink-0 mt-0.5" strokeWidth={1.8} />
        <div className="flex-1">
          <h3 className="font-display text-base sm:text-xl font-black leading-snug text-white">
            {isEg ? "مش جاهز تشتري دلوقتي؟" : "لست مستعداً للشراء الآن؟"}
            <span className="block text-[#C5A059] text-sm sm:text-lg font-bold mt-1">
              {isEg
                ? "احفظ الكتاب في قائمتك + ابعتلي العينة على بريدي"
                : "احفظ الكتاب في قائمتي + أرسل العينة على بريدي"}
            </span>
          </h3>
          <p className="mt-1.5 text-[12px] sm:text-sm text-[#A1B0C6] leading-relaxed">
            {isEg
              ? "هنبعتلك العينة الكاملة PDF + نص الفيديو + لو حصل عرض خصم خاص نخبرك أول واحد."
              : "نرسل لك العينة الكاملة PDF + نص الفيديو + إذا توفّر عرض خصم خاص نخبرك أولاً."}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-4 flex flex-col sm:flex-row gap-2.5">
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="example@email.com"
          aria-label="البريد الإلكتروني"
          data-testid="sample-lead-email-input"
          className="bg-[#040914]/80 border-[#C5A059]/30 text-white placeholder:text-[#A1B0C6]/50 flex-1"
          style={{ direction: "ltr", textAlign: "right" }}
        />
        <Button
          type="submit"
          disabled={submitting}
          data-testid="sample-lead-submit-btn"
          className="bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-bold rounded-sm px-5 py-5 sm:py-3 shrink-0"
        >
          {submitting ? (
            <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> جارٍ الحفظ...</span>
          ) : (
            <span className="flex items-center gap-2">
              <Bookmark className="w-4 h-4" />
              {isEg ? "ابعتلي العينة" : "أرسل لي العينة"}
            </span>
          )}
        </Button>
      </form>
      <p className="mt-3 text-[10px] sm:text-[11px] text-[#A1B0C6]/85 text-center">
        {isEg
          ? "نحترم خصوصيتك — مفيش spam ولا مشاركة لبريدك مع أي طرف."
          : "نحترم خصوصيتك — لا spam ولا مشاركة لبريدك مع أي طرف."}
      </p>
    </div>
  );
}
